import { useState, useCallback } from 'react'
import { RankedCamera } from '../utils/CameraRankingUtils'
import { useAppStore } from '@store/appStore'

/**
 * State for camera discovery mode
 */
interface DiscoveryState {
  visible: boolean
  cameras: RankedCamera[]
  radiusPx?: number
  selectedFrameIndex?: number
  projections?: Map<number, [number, number]>
}

/**
 * Hook for managing camera discovery mode
 * 
 * Handles showing/hiding thumbnail list (permanent visibility until manually closed)
 */
export function useCameraDiscovery() {
  const [state, setState] = useState<DiscoveryState>({
    visible: false,
    cameras: [],
    radiusPx: undefined,
    selectedFrameIndex: undefined
  })

  /**
   * Show thumbnail list with cameras (permanent visibility)
   */
  const showThumbnails = useCallback((cameras: RankedCamera[], radiusPx?: number, selectedFrameIndex?: number, projections?: Map<number, [number, number]>) => {
    console.log(`📸 [Discovery Hook] Showing ${cameras.length} camera thumbnails (permanent visibility), radiusPx: ${radiusPx?.toFixed(1) || 'undefined'}, selectedFrame: ${selectedFrameIndex ?? 'undefined'}, projections: ${projections?.size ?? 'undefined'}`)
    
    setState({
      visible: true,
      cameras,
      radiusPx,
      selectedFrameIndex,
      projections
    })
  }, [])

  /**
   * Update radius for existing thumbnail list (keeps camera list unchanged)
   */
  const updateRadius = useCallback((radiusPx: number) => {
    setState(prev => {
      if (!prev.visible || prev.cameras.length === 0) return prev
      console.log(`📸 [Discovery Hook] Updating radius: ${radiusPx.toFixed(1)}px (keeping ${prev.cameras.length} cameras)`)
      
      // Persist to AppStore for next tap
      useAppStore.getState().setPreferredDiscoveryRadiusPx(radiusPx)
      
      return {
        ...prev,
        radiusPx
      }
    })
  }, [])

  /**
   * Hide thumbnail list (manual close only)
   */
  const hideThumbnails = useCallback(() => {
    console.log(`📸 [Discovery Hook] Close button clicked - hiding thumbnails`)
    console.log(`📸 [Discovery Hook] Manually hiding thumbnails`)

    setState({
      visible: false,
      cameras: [],
      radiusPx: undefined,
      selectedFrameIndex: undefined,
      projections: undefined
    })
  }, [])

  /**
   * Handle camera selection from thumbnails (keeps thumbnails visible)
   */
  const selectCamera = useCallback((cameraId: number, projectedPixel?: [number, number]) => {
    console.log(`📸 [Camera Discovery] Selected camera: ${cameraId}`, projectedPixel ? `at pixel ${projectedPixel}` : '')
    
    const appState = useAppStore.getState()
    
    // Set the manually selected frame
    appState.setManuallySelectedFrame(cameraId)
    
    // Center the viewport on the selected frame (moves it under holy red dot)
    // Use skipAutoSelect to prevent auto-selection from overriding this choice
    appState.centerViewportOnFrame(cameraId, undefined, undefined, { skipAutoSelect: true })
    
    // Keep thumbnails visible for continued exploration
    // hideThumbnails() // Removed - permanent visibility
  }, [])

  return {
    state,
    showThumbnails,
    updateRadius,
    hideThumbnails,
    selectCamera
  }
}